import { fromWindAngleToDirection } from '@weenat/client/dist/core/metrics'
import { MetricSummaryValue as SummaryValue } from '@weenat/client/dist/core/metrics/useMetricSummary'
import { minVoltageByLevel } from '@weenat/client/dist/core/units/converters'
import { isMetric } from '@weenat/client/dist/enums/MetricIds'
import { useConvertedValue } from '@weenat/client/dist/hooks'
import { DeviceAvailableMeasure } from '@weenat/client/dist/resources/devices'
import { PlotAvailableMeasure } from '@weenat/client/dist/resources/plots'
import { useIntl } from '@weenat/wintl'
import useDisclosure from 'app/src/hooks/useDisclosure'
import { RestrictionReason } from 'app/src/hooks/useIsAppAccessRestricted'
import DewRiskInfo from 'app/src/kit/DewRiskInfo'
import Icons from 'app/src/kit/Icons'
import { LockedCarouselValueModal } from 'app/src/kit/LockedFeat'
import Text from 'app/src/kit/Text'
import WindDirectionIcon from 'app/src/kit/icons/WindDirectionIcon'
import isNil from 'lodash-es/isNil'
import moment from 'moment-timezone'
import { CSSProperties } from 'react'

interface MetricSummaryValueProps {
  metric: DeviceAvailableMeasure | PlotAvailableMeasure
  isShrunk?: boolean
  isMainValue?: boolean
  summaryValue: SummaryValue
  isRestricted?: boolean
  reason?: RestrictionReason
  showLabel?: boolean
  labelStyle?: CSSProperties | undefined
}

const MetricSummaryValue: FC<MetricSummaryValueProps> = ({
  metric,
  isShrunk = false,
  isMainValue,
  summaryValue,
  isRestricted,
  reason,
  showLabel = true,
  labelStyle
}) => {
  const { t } = useIntl()
  const { getUnit, convertValue } = useConvertedValue()

  const {
    aggregationId,
    fromForecasts,
    hasDewRisk,
    label,
    renderValue,
    timestamp,
    value,
    aggregationLabel
  } = summaryValue

  const nullValueColor = isNil(value) ? 'grayscale.300' : undefined
  const metricColor = isRestricted ? 'grayscale.300' : (`metrics.${metric}.500` as const)
  const unit = getUnit(metric)

  const valueSize = isShrunk ? 'lg' : isMainValue ? 'xl' : 'rg'
  const valueWeight = isShrunk || isMainValue ? 'bold' : 'regular'

  const isCurrentTemperature = metric === 'T' && aggregationId === 'latest'
  const { close, isOpen, open } = useDisclosure()

  return (
    <Flex
      key={`${aggregationId}-${metric}`}
      onClick={
        isRestricted
          ? (e) => {
              e.stopPropagation()
              e.preventDefault()
              open()
            }
          : undefined
      }
      $flexDirection='column'
      $justifyContent={isShrunk ? 'space-between' : ''}
    >
      {showLabel && (
        <Flex $mb='md'>
          <Text
            $lineHeight='none'
            $fontSize='sm'
            $fontWeight='regular'
            $color={metricColor}
            style={labelStyle}
          >
            {label}
          </Text>
        </Flex>
      )}
      <Flex>
        {isRestricted ? (
          <Flex $alignItems='flex-end'>
            <Icons.Lock $color={'grayscale.300'} $size='md' />
            <Text $lineHeight='sm' $fontSize='sm' $color={'grayscale.300'}>
              {t(`units.${unit}_abbr`)}
            </Text>
          </Flex>
        ) : aggregationId === 'LW_V' ? (
          <>
            {!isNil(value) ? (
              <Flex>
                <Text $fontSize='lg' $fontWeight='bold' $lineHeight='xs'>
                  {value >= minVoltageByLevel[1]
                    ? t('dss.leafWetness.flags.wet', { capitalize: true })
                    : t('dss.leafWetness.flags.dry', { capitalize: true })}
                </Text>
              </Flex>
            ) : (
              <Text $lineHeight='xs' $color={'grayscale.300'}>
                -
              </Text>
            )}
          </>
        ) : metric === 'PPFD' ? (
          <>
            {!isNil(value) ? (
              <Flex $gap='sm' $alignItems='center'>
                <Text $fontSize='lg' $fontWeight='bold' $lineHeight='xs'>
                  {value}
                </Text>
                <Text $fontSize='md' $fontWeight='bold' $lineHeight='xs'>
                  {t(`units.${unit}_abbr`)}
                </Text>
              </Flex>
            ) : (
              <Text $lineHeight='xs' $color={'grayscale.300'}>
                -
              </Text>
            )}
          </>
        ) : aggregationId === 'cumulativeHours' ? (
          <>
            {!isNil(value) ? (
              <Flex>
                <Text $fontSize='lg' $fontWeight='bold' $lineHeight='xs'>
                  {value}
                  {t('units.hours_abbr')}
                </Text>
              </Flex>
            ) : (
              <Text $lineHeight='xs' $color={'grayscale.300'}>
                -
              </Text>
            )}
          </>
        ) : aggregationId === 'lastSlidingWeek' || aggregationId === 'nextSlidingWeek' ? (
          <>
            {!isNil(value) ? (
              <Flex>
                <Text $fontSize='lg' $fontWeight='bold' $lineHeight='xs'>
                  {convertValue({
                    metric: 'RR',
                    value: value
                  })}
                </Text>
              </Flex>
            ) : (
              <Text $lineHeight='xs' $color={'grayscale.300'}>
                -
              </Text>
            )}
          </>
        ) : renderValue ? (
          renderValue(value)
        ) : aggregationId === 'DD' ? (
          <>
            {!isNil(value) ? (
              <Flex $alignItems='flex-end' $gap='sm'>
                <Text $fontSize={'md'} $fontWeight='bold' $lineHeight={'xs'}>
                  {t(`windDirections.${fromWindAngleToDirection(value)}`)}
                </Text>
                <WindDirectionIcon windOrigin={value} $size='md' $rounded={false} />
              </Flex>
            ) : (
              <Text $lineHeight='xs' $color={'grayscale.300'}>
                -
              </Text>
            )}
          </>
        ) : isCurrentTemperature ? (
          <>
            <Box>
              <Flex>
                <Text
                  $fontSize={valueSize}
                  $fontWeight={valueWeight}
                  $lineHeight='xs'
                  $color={nullValueColor}
                  $fontVariantNumeric='tabular-nums'
                >
                  {!isNil(value)
                    ? convertValue({
                        value,
                        metric: isMetric(aggregationId) ? aggregationId : metric,
                        displayUnit: isShrunk || !isMainValue,
                        abbreviatedUnit: true
                      })
                    : '-'}
                </Text>
                {isMainValue && !isShrunk && t(`units.${unit}_abbr`).length !== 0 ? (
                  <Box $ml={1}>
                    <Text $fontSize='sm' $color={nullValueColor}>
                      {t(`units.${unit}_abbr`)}
                    </Text>
                  </Box>
                ) : null}
              </Flex>
              {hasDewRisk ? <DewRiskInfo showLabel /> : null}
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Text
                $fontSize={valueSize}
                $fontWeight={valueWeight}
                $lineHeight='xs'
                $color={nullValueColor}
                $fontVariantNumeric='tabular-nums'
              >
                {!isNil(value)
                  ? convertValue({
                      value,
                      metric: isMetric(aggregationId) ? aggregationId : metric,
                      displayUnit: isShrunk || !isMainValue,
                      abbreviatedUnit: true
                    })
                  : '-'}
              </Text>
              {!isMainValue && !isNil(timestamp) && !isShrunk && (
                <Text $fontSize='xs' $fontStyle='italic' $color={'grayscale.black'}>
                  {t(fromForecasts ? 'summaries.expected_at' : 'summaries.measured_at', {
                    time: moment.unix(parseInt(timestamp, 10)).format(t('formats.time'))
                  })}
                </Text>
              )}
              {!isMainValue && !isNil(aggregationLabel) && !isShrunk && (
                <Text $fontSize='xs' $fontStyle='italic' $color={'grayscale.800'}>
                  {aggregationLabel}
                </Text>
              )}
            </Box>

            {isMainValue && t(`units.${unit}_abbr`).length !== 0 && !isShrunk && (
              <Box $ml={1}>
                <Text $fontSize='sm' $color={nullValueColor}>
                  {t(`units.${unit}_abbr`)}
                </Text>
              </Box>
            )}
          </>
        )}
      </Flex>
      {isRestricted && (
        <LockedCarouselValueModal close={close} isOpen={isOpen} reason={reason ?? 'freemium'} />
      )}
    </Flex>
  )
}

export default MetricSummaryValue
